import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { GiftCardStepEnum } from '../models/giftcard-step.enum';
import { Product } from '../../order/models/product';
import { OrderTypeEnum } from '../../order/models/order-type.enum';
import { OrderStateService } from '../../order/services/order-state.service';
import { OrderLineType } from '../../order/models/order-line-type';
import { LoginStateService } from '../../login/services/login-state.service';
import { Order } from '../../order/models/order';
import { ProductsStateService } from '../../order/services/products-state.service';
import { LoggerService } from '../../../services/logger/logger.service';
import { InactiveTimerService } from '../../../services/inactive-timer.service';
import { ShopSettingsService } from '../../general/services/shop-settings.service';
import { PartnerStateService } from '../../partner/services/partner-state.service';
import { PartnerCodeStateService } from '../../partner-code/services/partner-code-state.service';
import { catchError, map, switchMap, takeWhile, tap } from 'rxjs/operators';
import { OrderLine } from '../../order/models/order-line';
import { VuConnectionHttpService } from '../../vu/services/vu-connection-http.service';
import { BaseStepService } from '../../general/services/base-step.services';

@Injectable()
export class GiftCardStepService extends BaseStepService<GiftCardStepEnum> {

  private _giftCardProducts: Product[];
  private _customGiftCardProduct: Product[];
  private _selectedGiftCardId: number;
  private _giftCardCustomPrice: number;

  cardNumber: string;

  constructor(
    protected logger: LoggerService,
    protected inactiveTimerService: InactiveTimerService,
    protected shopSettingsService: ShopSettingsService,
    protected partnerStateService: PartnerStateService,
    protected partnerCodeStateService: PartnerCodeStateService,
    private router: Router,
    private orderStateService: OrderStateService,
    private loginStateService: LoginStateService,
    private productsStateService: ProductsStateService,
    private vuConnectionHttpService: VuConnectionHttpService,
  ) {
    super(
      logger,
      inactiveTimerService,
      shopSettingsService,
      partnerStateService,
      partnerCodeStateService
    );
    this._steps = [
      GiftCardStepEnum.SelectGiftCard,
      GiftCardStepEnum.Login,
      GiftCardStepEnum.Payment
    ];
  }

  protected get serviceName(): string {
    return GiftCardStepService.name;
  }

  protected get initStep(): GiftCardStepEnum {
    return GiftCardStepEnum.Init;
  }

  protected get resetStep(): GiftCardStepEnum {
    return GiftCardStepEnum.Reset;
  }

  get selectedGiftCardId(): number {
    return this._selectedGiftCardId;
  }

  set selectedGiftCardId(productId: number) {
    this._selectedGiftCardId = productId;
  }

  get giftCardCustomPrice(): number {
    return this._giftCardCustomPrice;
  }

  set giftCardCustomPrice(customPrice: number) {
    this._giftCardCustomPrice = customPrice;
  }

  get existGiftCards(): boolean {
    return (this._giftCardProducts != null && this._giftCardProducts.length > 0)
      ||
      (this._customGiftCardProduct != null && this._customGiftCardProduct.length > 0);
  }

  get giftCardProducts(): Product[] {
    return this._giftCardProducts;
  }

  getGiftCardProductById(id: number): Product {
    if (!this._giftCardProducts) {
      return null;
    }

    return this._giftCardProducts.find(x => x.id === id);
  }

  get customGiftCardProduct(): Product[] {
    return this._customGiftCardProduct;
  }

  get isLoggedIn(): boolean {
    return this.loginStateService.isLoggedIn;
  }

  get isOrderAvailable(): boolean {
    return this.step !== GiftCardStepEnum.Reset;
  }

  get isPaymentPageAvailable(): boolean {

    if (this.step < GiftCardStepEnum.Login) {
      return false;
    }

    const order = this.orderStateService.order;

    if (!order || order.orderType !== OrderTypeEnum.Giftcard) {
      return false;
    }

    // service fee + one gift card line
    return order.orderLines.length > 0;
  }

  addGiftCardCustomPrice(amount: number): OrderLine {
    const giftcards = this.customGiftCardProduct;
    if (!giftcards || giftcards.length === 0) {
      return null;
    }
    const giftcardProduct = giftcards[0];
    const order = this.orderStateService.order;
    return order.insertOrderLine(
      OrderLineType.GiftCard,
      0,
      giftcardProduct.name,
      giftcardProduct.id,
      amount,
      giftcardProduct.taxRate,
      1,
      false
    );
  }

  private _updateGiftcardOrder(productId: number, customPrice?: number): void {

    const order = this.orderStateService.order;

    if (customPrice && this.addGiftCardCustomPrice(customPrice)) {
      this.router.navigateByUrl('giftcard/order');
      return;
    }

    if (productId && !customPrice) {
      const product = this.getGiftCardProductById(productId);
      if (product) {
        const orderLine = order.insertOrderLine(
          OrderLineType.GiftCard,
          0,
          product.name,
          product.id,
          product.price,
          product.taxRate,
          1,
          false);

        orderLine.product = product;
      }
    }
    this.router.navigateByUrl('giftcard/order');
  }

  private _showGiftCardListComponent(): void {
    this.router.navigate(['giftcard']);
  }

  private _showGiftcardOrderComponent(productId: number, customPrice?: number): void {
    const order = this.orderStateService.order;

    if (!order || order.orderType !== OrderTypeEnum.Giftcard) {
      this.orderStateService.initialize(OrderTypeEnum.Giftcard).subscribe(
        done => {
          this._updateGiftcardOrder(productId, customPrice);
        }
      );
    } else {
      this._updateGiftcardOrder(productId, customPrice);
    }
  }

  private _showGiftcardLoginComponent(): void {
    this.router.navigate(['giftcard/login']);
  }

  private _showGiftcardRegisterComponent(): void {
    this.router.navigate(['giftcard/register']);
  }

  private _showGiftcardPaymentComponent(): void {
    if (this.unlimitedAmount
      && this._customGiftCardProduct
      && this._customGiftCardProduct.length !== 0
      && this._customGiftCardProduct[0]
      && this.partnerStateService.loggedInPartner
      && this.cardNumber
    ) {
      this.vuConnectionHttpService.redirectToExternalUnlimitedRechargeCard(
        this._customGiftCardProduct[0].id,
        this.cardNumber,
        this.partnerStateService.loggedInPartner.barcode,
        this.partnerStateService.loggedInPartner.accountBalance,
        this.partnerStateService.loggedInPartner.name
      );
      return;
    }

    this.router.navigate(['payment/start']);
  }

  protected _internalInitialize(): void {
    super._internalInitialize();
    this.cardNumber = '';
    // this.orderStateService.initialize(OrderTypeEnum.Giftcard);
  }

  initialize(): Observable<boolean> {
    return super.initialize().pipe(
      tap(_ => {
        this.selectedGiftCardId = null;
        this.giftCardCustomPrice = null;
      }),
    );
  }

  protected _clearCache(): void {
    this._giftCardProducts = null;
    this._customGiftCardProduct = null;
  }

  protected _fillCache(): Observable<boolean> {
    return of(true).pipe(
      switchMap(_ => {
        if (this._giftCardProducts) {
          return of(_);
        }
        return this.productsStateService.giftcardProducts.pipe(
          map(allGiftCards => {
            this.logger.info(`${this.serviceName}: initialize giftcard products received.`);
            this._fillGiftCardProducts(allGiftCards);
            return _;
          }),
          catchError(error => {
            this.logger.info(`${this.serviceName}: initialize giftcard products receive error ${error}.`);
            return throwError(error);
          }),
        );
      }),
      switchMap(_ => {
        return this.productsStateService.customGiftCardProduct.pipe(
          map(allCustomGiftCards => {
            this.logger.info(`${this.serviceName}: initialize custom giftcard products received.`);
            this._fillCustomGiftCardProducts(allCustomGiftCards );
            return _;
          }),
          catchError(error => {
            this.logger.info(`${this.serviceName}: initialize custom giftcard products receive error ${error}.`);
            return throwError(error);
          }),
        );
      }),
    );
  }

  private _fillGiftCardProducts(allGiftCards: Product[]): void {
    this._giftCardProducts = allGiftCards ? allGiftCards.sort((n1, n2) => n1.sequence - n2.sequence): allGiftCards;
  }

  private _fillCustomGiftCardProducts(allCustomGiftCards: Product[]): void {
    this._customGiftCardProduct = allCustomGiftCards;
  }

  get unlimitedAmount(): boolean {
    return this.shopSettingsService.isVUMode
      && (!this._giftCardProducts || this._giftCardProducts.length === 0)
      && this._customGiftCardProduct
      && this._customGiftCardProduct.length !== 0;
  }

  isAvailableStep(step: GiftCardStepEnum): boolean {
    if (!step) {
      return false;
    }

    switch (step) {
      case GiftCardStepEnum.Login:
      case GiftCardStepEnum.Register:
      case GiftCardStepEnum.RegisterAsGuest:
        return this.needAuth;
      default:
        return true;
    }
  }

  setStep(step: GiftCardStepEnum): void {
    this._step = step;
    this._stepIndex = this._steps.indexOf(step);
  }

  update(
    step: GiftCardStepEnum = GiftCardStepEnum.Reset,
  ): void {

    super.update(step);
    this.inactiveTimerService.resetTimer();

    switch (this._step) {
      case GiftCardStepEnum.Init:
        this._showGiftCardListComponent();
        break;
      case GiftCardStepEnum.SelectGiftCard:
        this._showGiftcardOrderComponent(this.selectedGiftCardId, this.giftCardCustomPrice);
        this.selectedGiftCardId = null;
        break;
      case GiftCardStepEnum.Login:
        this._showGiftcardLoginComponent();
        break;
      case GiftCardStepEnum.Register:
      case GiftCardStepEnum.RegisterAsGuest:
        this._showGiftcardRegisterComponent();
        break;
      case GiftCardStepEnum.Payment:
        this._showGiftcardPaymentComponent();
        break;
      case GiftCardStepEnum.Reset:
      default:
        this.initialize().subscribe();
        break;
    }
  }

  createOrder(): Observable<Order> {
    return of({}).pipe(
      takeWhile(_ => this.isPaymentPageAvailable),
      switchMap(_ => this.orderStateService.createOrder()),
      switchMap(order => this.initialize().pipe(map(_ => order))),
    );
  }

  updateOrderLineProducts(): void {
    const order = this.orderStateService.order;
    if (!order || !order.orderLines || order.orderLines.length === 0) {
      return;
    }

    for (const line of order.orderLines) {
      const product = this.getGiftCardProductById(line.productId);
      if (product) {
        line.product = product;
      }
    }
  }
}
